// src/components/proposal-formats/PostSaleTodoStepRow.tsx

import { useEffect, useState } from "react";
import type { OppTodoTemplateStep } from "@/lib/opportunity/oppTodoTemplateTypes";
import type { UpsertOppTodoTemplateStepPayload } from "@/lib/opportunity/oppTodoTemplateClient";

type Props = {
  step: OppTodoTemplateStep;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSave: (payload: UpsertOppTodoTemplateStepPayload) => Promise<{ ok: boolean; error?: string }>;
  onDelete: (id: string) => Promise<{ ok: boolean; error?: string }>;
  onMove: (direction: "up" | "down") => void;
};

export function PostSaleTodoStepRow(props: Props) {
  const { step, canMoveUp, canMoveDown, onSave, onDelete, onMove } = props;
  const [label, setLabel] = useState(step.label);
  const [dueOffset, setDueOffset] = useState(
    step.default_due_offset_days == null ? "" : String(step.default_due_offset_days),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLabel(step.label);
    setDueOffset(step.default_due_offset_days == null ? "" : String(step.default_due_offset_days));
  }, [step.id, step.label, step.default_due_offset_days]);

  async function handleSave() {
    setBusy(true);
    setError(null);
    const parsedOffset =
      dueOffset.trim() === "" ? null : Number.parseInt(dueOffset.trim(), 10);
    if (dueOffset.trim() !== "" && (!Number.isFinite(parsedOffset) || parsedOffset! < 0)) {
      setBusy(false);
      setError("Due offset must be a non-negative integer or blank.");
      return;
    }
    const r = await onSave({
      id: step.id,
      label,
      sort_order: step.sort_order,
      default_due_offset_days: parsedOffset,
    });
    setBusy(false);
    if (!r.ok) setError(r.error ?? "Could not save step.");
  }

  async function handleDelete() {
    if (!window.confirm("Remove this template step?")) return;
    setBusy(true);
    setError(null);
    const r = await onDelete(step.id);
    setBusy(false);
    if (!r.ok) setError(r.error ?? "Could not delete step.");
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/40">
      <div className="flex flex-wrap items-start gap-2">
        <div className="flex shrink-0 flex-col gap-0.5 pt-1">
          <button
            type="button"
            disabled={!canMoveUp || busy}
            onClick={() => onMove("up")}
            className="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] disabled:opacity-40 dark:border-zinc-600"
            aria-label="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={!canMoveDown || busy}
            onClick={() => onMove("down")}
            className="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] disabled:opacity-40 dark:border-zinc-600"
            aria-label="Move down"
          >
            ↓
          </button>
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            placeholder="Step label"
          />
          <label className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-300">
            <span className="shrink-0">Default due (days after order)</span>
            <input
              type="number"
              min={0}
              max={365}
              value={dueOffset}
              onChange={(e) => setDueOffset(e.target.value)}
              className="w-20 rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-zinc-600 dark:bg-zinc-900"
              placeholder="—"
            />
          </label>
        </div>

        <div className="flex shrink-0 flex-col gap-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleDelete()}
            className="rounded-lg border border-red-200 px-2.5 py-1 text-[11px] text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-950/40"
          >
            Remove
          </button>
        </div>
      </div>
      {error ? <p className="mt-2 text-[11px] text-red-700 dark:text-red-200">{error}</p> : null}
    </div>
  );
}
